import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer, Alert, Button, Select, Input, DataTable, Skeleton, EmptyState, ErrorBoundary, Modal, useToast,
  FilterBar, SavedFiltersMenu, ListSummaryBar,
} from '@bbpos/ui';
import type { ColumnDef, FilterBarFacet } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { GLASS } from '@/constants/design-tokens';
import {
  useSalesInvoices, useSalesInvoiceStats, useRecordSalesAdvance, useDownloadSalesInvoice, SalesInvoiceStatusBadge,
  salesInvoicesApi,
} from '@/features/sales-invoices';
import { useListFilterBar } from '@/features/saved-filters';
import { downloadFile } from '@/lib/download-file';
import type { SalesInvoiceListItem, SalesInvoiceStatus, SalesPaymentMethod } from '@/features/sales-invoices';
import { useSalesOrders } from '@/features/sales-orders';
import { PoKpi } from '@/features/purchase-orders';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { toLocalIsoDate } from '../purchase-orders/date-utils';
import { ClipboardIcon, ClockIcon, CheckCircleIcon, EyeIcon, DownloadIcon } from '../purchase-orders/icons';
import { CurrencyIcon } from '../quotations/icons';

// T27: the shared filter bar. Keys are the list endpoint's own parameters.
export const SI_FILTER_KEYS = [
  'customer_name', 'mobile', 'invoice_number', 'search', 'status', 'overdue', 'start_date', 'end_date',
];
const SI_FACETS: FilterBarFacet[] = [
  { key: 'customer_name', label: 'Customer' },
  { key: 'mobile', label: 'Mobile', kind: 'phone' },
  { key: 'invoice_number', label: 'Invoice no.', kind: 'number' },
  { key: 'search', label: 'Invoice, SO, customer or PO no.' },
];
const SI_DATE = { fromKey: 'start_date', toKey: 'end_date', label: 'Invoice date' };

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'PARTIALLY_PAID', label: 'Partially paid' },
  { value: 'PAID', label: 'Paid' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const METHOD_OPTIONS: { value: SalesPaymentMethod; label: string }[] = [
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CARD', label: 'Card' },
];

function fmtDate(value: string | null) {
  return value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}

// Hand a streamed PDF to the browser as a download (filename from Content-Disposition).
function saveBlob({ blob, filename }: { blob: Blob; filename?: string }, fallback: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? fallback;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function Header({ isManager, onAdvance }: { isManager: boolean; onAdvance: () => void }) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-teal-500 via-success-500 to-brand-600 p-6 text-white shadow-[0_24px_60px_-24px_rgba(20,184,166,0.55)] mb-6 animate-fade-in">
      <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
      <div className="relative flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white">Sales Invoices</h1>
          <p className="text-teal-50 mt-1">What customers owe you for goods sold on sales orders, and what has been received</p>
        </div>
        {isManager && (
          <Button variant="secondary" onClick={onAdvance} className="bg-white/95 text-teal-700 hover:bg-white">
            <CurrencyIcon className="h-4 w-4 mr-1.5" />
            Record advance
          </Button>
        )}
      </div>
    </div>
  );
}

/** Advance = money received on a sales order before (or beyond) its invoices; it settles the next invoice raised. */
function RecordAdvanceModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const dp = getCurrencyDecimals(currency);
  // Sales orders take a single status filter, so fetch the recent ones and keep those with a receivable balance.
  const { data: orders, isLoading } = useSalesOrders({ page: 1, limit: 50 });
  const advanceMutation = useRecordSalesAdvance();
  const [soId, setSoId] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<SalesPaymentMethod>('BANK_TRANSFER');
  const [date, setDate] = useState(toLocalIsoDate(new Date()));
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) { setSoId(''); setAmount(''); setMethod('BANK_TRANSFER'); setDate(toLocalIsoDate(new Date())); setReference(''); setNotes(''); }
  }, [open]);

  const candidates = (orders?.data ?? []).filter((o) => o.status !== 'CANCELLED' && o.grand_total - o.paid_total > 0);
  const selected = candidates.find((o) => o.id === soId);
  const cap = selected ? selected.grand_total - selected.paid_total : 0;

  function pick(id: string) {
    setSoId(id);
    const o = candidates.find((c) => c.id === id);
    setAmount(o ? ((o.grand_total - o.paid_total) / subunit).toFixed(dp) : '');
  }

  function submit() {
    if (!selected) { toast({ title: 'Choose the sales order the advance is for', variant: 'error' }); return; }
    const paise = Math.round((parseFloat(amount) || 0) * subunit);
    if (paise <= 0 || paise > cap) { toast({ title: `Amount must be between ${formatCurrency(1, currency)} and ${formatCurrency(cap, currency)}`, variant: 'error' }); return; }
    advanceMutation.mutate(
      { soId: selected.id, payload: { amount: paise, payment_method: method, paid_on: date || undefined, reference: reference.trim() || undefined, notes: notes.trim() || undefined } },
      {
        onSuccess: (p) => { toast({ title: `Advance of ${formatCurrency(p.amount, currency)} recorded against ${selected.so_number}`, variant: 'success' }); onClose(); },
        onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
      },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Record advance" allowOverflow
      footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={onClose}>Cancel</Button><Button variant="primary" onClick={submit} loading={advanceMutation.isPending}>Save advance</Button></div>}>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Sales order</label>
          <Select
            variant="searchable"
            loading={isLoading}
            options={candidates.map((o) => ({ value: o.id, label: `${o.so_number} · ${o.customer_name} · ${formatCurrency(o.grand_total - o.paid_total, currency)} receivable` }))}
            value={soId}
            onChange={(v) => pick(typeof v === 'string' ? v : '')}
            placeholder="Search by SO number or customer"
          />
          {!isLoading && candidates.length === 0 && <p className="text-xs text-neutral-500 mt-1">No open sales orders with a receivable balance.</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Amount ({symbol})</label>
          <Input type="number" value={amount} onChange={setAmount} />
          {selected && <p className="text-xs text-neutral-500 mt-1">Receivable {formatCurrency(cap, currency)}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Method</label>
          <Select options={METHOD_OPTIONS} value={method} onChange={(v) => setMethod((typeof v === 'string' ? v : 'BANK_TRANSFER') as SalesPaymentMethod)} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Received on</label>
          <Input type="date" value={date} onChange={setDate} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Reference</label>
          <Input value={reference} onChange={setReference} placeholder="UTR / cheque no." />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Notes</label>
          <Input value={notes} onChange={setNotes} placeholder="Optional" />
        </div>
        <p className="col-span-2 text-xs text-neutral-500">The advance settles the order's next invoice automatically; until then it shows as held.</p>
      </div>
    </Modal>
  );
}

function SalesInvoiceListContent() {
  const currency = useTenantCurrency();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);
  const isManager = (ROLE_LEVELS[profile?.role ?? ''] ?? 0) >= ROLE_LEVELS.MANAGER;

  const bar = useListFilterBar({ page: 'sales_invoices', keys: SI_FILTER_KEYS, date: SI_DATE });
  const { values } = bar;
  const page = Number(searchParams.get('page') ?? '1');

  const [showAdvance, setShowAdvance] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const pdfMutation = useDownloadSalesInvoice();

  const listParams = {
    status: values.status as SalesInvoiceStatus | undefined,
    search: values.search,
    customer_name: values.customer_name,
    mobile: values.mobile,
    invoice_number: values.invoice_number,
    start_date: values.start_date,
    end_date: values.end_date,
    overdue: values.overdue === 'true' || undefined,
  };
  const { data, isLoading, isError, error, refetch } = useSalesInvoices({ ...listParams, page, limit: 20 });

  // Every matching invoice, all pages — the API writes the file (T27).
  const handleExport = async () => {
    setIsExporting(true);
    try {
      downloadFile(await salesInvoicesApi.exportCsv(listParams), 'sales-invoices.csv');
    } catch (err) {
      toast({ title: (err as Error).message || 'Export failed', variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };
  const { data: stats } = useSalesInvoiceStats();

  useEffect(() => { if (error) toast({ title: (error as Error).message, variant: 'error' }); }, [error]);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  function handleDownload(inv: SalesInvoiceListItem) {
    pdfMutation.mutate(inv.id, {
      onSuccess: (r) => saveBlob(r, `${inv.invoice_number}.pdf`),
      onError: (e: Error) => toast({ title: e.message || 'Failed to download the invoice', variant: 'error' }),
    });
  }

  const invoices = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const today = toLocalIsoDate(new Date());

  const columns: ColumnDef<SalesInvoiceListItem>[] = [
    { key: 'invoice_number', header: 'Invoice #', render: (_v, r) => (
      <button type="button" onClick={() => navigate(`/sales-invoices/${r.id}`)} className="text-sm text-teal-600 dark:text-teal-400 hover:underline font-medium font-mono tabular-nums">{r.invoice_number}</button>
    ) },
    { key: 'so_number', header: 'Sales order', render: (_v, r) => (
      <button type="button" onClick={() => navigate(`/sales-orders/${r.sales_order_id}`)} className="text-sm text-brand-600 dark:text-brand-400 hover:underline font-mono tabular-nums">{r.so_number}</button>
    ) },
    { key: 'customer_name', header: 'Customer', render: (_v, r) => <span className="text-sm text-neutral-700 dark:text-neutral-300">{r.customer_name}</span> },
    { key: 'customer_po_number', header: 'Customer PO', render: (_v, r) => <span className="text-sm font-mono text-neutral-600 dark:text-neutral-400">{r.customer_po_number}</span> },
    { key: 'invoice_date', header: 'Invoice date', render: (_v, r) => <span className="text-sm tabular-nums text-neutral-600 dark:text-neutral-400">{fmtDate(r.invoice_date)}</span> },
    { key: 'due_date', header: 'Due', render: (_v, r) => {
      const isOverdue = r.due_date && r.due_date.slice(0, 10) < today && (r.status === 'OPEN' || r.status === 'PARTIALLY_PAID');
      return <span className={`text-sm tabular-nums ${isOverdue ? 'text-danger-600 font-medium' : 'text-neutral-600 dark:text-neutral-400'}`}>{fmtDate(r.due_date)}</span>;
    } },
    { key: 'total_amount', header: 'Total', numeric: true, render: (_v, r) => <span className="text-sm tabular-nums text-neutral-800 dark:text-neutral-200">{formatCurrency(r.total_amount, currency)}</span> },
    { key: 'balance_due', header: 'Balance', numeric: true, render: (_v, r) => (
      <span className={`text-sm font-semibold tabular-nums ${r.status !== 'CANCELLED' && r.balance_due > 0 ? 'text-neutral-900 dark:text-white' : 'text-neutral-400'}`}>
        {r.status === 'CANCELLED' ? '—' : formatCurrency(r.balance_due, currency)}
      </span>
    ) },
    { key: 'status', header: 'Status', render: (_v, r) => <SalesInvoiceStatusBadge status={r.status} /> },
    { key: 'actions', header: '', render: (_v, r) => (
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => navigate(`/sales-invoices/${r.id}`)} title="View" className="p-1.5 rounded-lg text-neutral-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"><EyeIcon className="h-4 w-4" /></button>
        <button type="button" onClick={() => handleDownload(r)} title="Download PDF" aria-label={`Download ${r.invoice_number}`} className="p-1.5 rounded-lg text-neutral-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"><DownloadIcon className="h-4 w-4" /></button>
      </div>
    ) },
  ];

  if (isLoading && !data) {
    return (<><Skeleton variant="rect" height={96} className="rounded-xl mb-6" /><Skeleton variant="rect" height={400} className="rounded-xl" /></>);
  }
  if (isError) {
    return (
      <>
        <Header isManager={isManager} onAdvance={() => setShowAdvance(true)} />
        <Alert variant="danger" title="Couldn't load sales invoices" description={(error as Error)?.message || 'The request failed — this is a server problem, not an empty list.'} action={{ label: 'Retry', onClick: () => void refetch() }} />
        <RecordAdvanceModal open={showAdvance} onClose={() => setShowAdvance(false)} />
      </>
    );
  }
  if (total === 0 && Object.keys(values).length === 0) {
    return (
      <>
        <Header isManager={isManager} onAdvance={() => setShowAdvance(true)} />
        <div className={`${GLASS} p-10`}>
          <EmptyState title="No sales invoices yet" description="Raise an invoice from a sales order once goods are delivered — in full or in part. Payments are recorded here." action={isManager ? { label: 'Open Sales Orders', onClick: () => navigate('/sales-orders') } : undefined} />
        </div>
        <RecordAdvanceModal open={showAdvance} onClose={() => setShowAdvance(false)} />
      </>
    );
  }

  return (
    <>
      <Header isManager={isManager} onAdvance={() => setShowAdvance(true)} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
        <PoKpi title="Outstanding" value={formatCurrency(stats?.outstanding_total ?? 0, currency)} tone="warning" icon={<ClipboardIcon className="h-5 w-5" />} subtext={`${(stats?.open_count ?? 0) + (stats?.partially_paid_count ?? 0)} open invoices`} delay={0} />
        <PoKpi title="Overdue" value={formatCurrency(stats?.overdue_total ?? 0, currency)} tone="danger" icon={<ClockIcon className="h-5 w-5" />} subtext={`${stats?.overdue_count ?? 0} invoices past due`} delay={60} />
        <PoKpi title="Received this month" value={formatCurrency(stats?.received_this_month ?? 0, currency)} tone="success" icon={<CheckCircleIcon className="h-5 w-5" />} subtext="incl. advances" delay={120} />
        <PoKpi title="Advances held" value={formatCurrency(stats?.advances_held ?? 0, currency)} tone="violet" icon={<CurrencyIcon className="h-5 w-5" />} subtext="not yet invoiced" delay={180} />
      </div>
      {/* Filters (T27) */}
      <div className="mb-4">
        <FilterBar
          facets={SI_FACETS}
          selects={[
            { key: 'status', label: 'Status', options: STATUS_OPTIONS, allLabel: 'All statuses' },
            { key: 'overdue', label: 'Due', options: [{ value: 'true', label: 'Overdue only' }], allLabel: 'Any' },
          ]}
          date={SI_DATE}
          values={values}
          onChange={bar.onChange}
          onReset={bar.onReset}
          timeZone={bar.timeZone}
          inputRef={searchRef}
          placeholder="Type a customer, mobile or invoice number…  ( press / )"
          favorites={<SavedFiltersMenu {...bar.favorites} />}
          actions={
            <Button variant="secondary" onClick={handleExport} disabled={total === 0 || isExporting}>
              {isExporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          }
        />
      </div>
      {/* Every matching invoice, not the page — the export's count and sums. */}
      <div className="mb-4">
        <ListSummaryBar
          loading={isLoading}
          items={[
            { label: 'Invoices matching', value: total.toLocaleString('en-IN'), tone: 'teal' },
            { label: 'Invoiced', value: formatCurrency(data?.meta?.summary?.total_amount ?? 0, currency), tone: 'brand' },
            { label: 'Balance due', value: formatCurrency(data?.meta?.summary?.balance_due ?? 0, currency), tone: 'warning' },
          ]}
        />
      </div>
      <div className={`${GLASS} animate-slide-up`}>
        <DataTable<SalesInvoiceListItem> columns={columns} data={invoices} loading={isLoading} totalCount={total} currentPage={page} pageSize={20}
          onPageChange={(p) => bar.onChange({ page: String(p) })}
          emptyState={{ title: 'No sales invoices match filters', description: 'Try adjusting your filters' }} />
      </div>
      <RecordAdvanceModal open={showAdvance} onClose={() => setShowAdvance(false)} />
    </>
  );
}

export function SalesInvoiceListPage() {
  return (
    <PageContainer title="Sales Invoices" breadcrumbs={[{ label: 'Finance' }, { label: 'Sales Invoices' }]}>
      <ErrorBoundary><SalesInvoiceListContent /></ErrorBoundary>
    </PageContainer>
  );
}
